import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { callCampaignsApi } from "@/api";
import type { CallCampaign, CallCampaignStatus, ChannelExportImportSummary } from "@/types";
import { Phone, Plus, Upload, X } from "lucide-react";
import { format } from "date-fns";

const statusColors: Record<CallCampaignStatus, string> = {
  DRAFT: "bg-slate-100 text-slate-700",
  SCHEDULED: "bg-blue-100 text-blue-700",
  RUNNING: "bg-green-100 text-green-700",
  PAUSED: "bg-yellow-100 text-yellow-700",
  COMPLETED: "bg-blue-100 text-blue-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const statusLabels: Record<CallCampaignStatus, string> = {
  DRAFT: "下書き",
  SCHEDULED: "予約済",
  RUNNING: "実行中",
  PAUSED: "一時停止",
  COMPLETED: "完了",
  CANCELLED: "キャンセル",
};

const audienceLabels = {
  CONTACT: "コンタクト",
  PROSPECT: "見込みリスト",
} as const;

function ProgressBar({ campaign }: { campaign: CallCampaign }) {
  if (campaign.totalCalls === 0) return null;
  const done = campaign.totalAnswered + campaign.totalFailed;
  const pct = Math.round((done / campaign.totalCalls) * 100);
  return (
    <div className="w-full bg-slate-200 rounded-full h-2">
      <div
        className="bg-blue-500 h-2 rounded-full transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function CallCampaigns() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const [importSource, setImportSource] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [importError, setImportError] = useState("");
  const [preview, setPreview] = useState<ChannelExportImportSummary | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["call-campaigns"],
    queryFn: () => callCampaignsApi.list(),
    refetchInterval: (query) => {
      const campaigns = query.state.data?.data || [];
      return campaigns.some((campaign) => campaign.status === "RUNNING") ? 3000 : false;
    },
  });

  const campaigns = data?.data || [];
  const buildImportPayload = (dryRun: boolean) => {
    const trimmed = importSource.trim();
    if (!trimmed) throw new Error("channel-export URL またはJSONを入力してください");
    const base = {
      dryRun,
      campaignName: campaignName.trim() || undefined,
    };
    if (trimmed.startsWith("{")) {
      return { ...base, payload: JSON.parse(trimmed) };
    }
    return { ...base, exportUrl: trimmed };
  };

  const previewMutation = useMutation({
    mutationFn: () => callCampaignsApi.importChannelExport(buildImportPayload(true)),
    onSuccess: (result) => {
      if ("summary" in result) {
        setPreview(result.summary);
        setImportError("");
      }
    },
    onError: (err) => {
      setPreview(null);
      setImportError(err instanceof Error ? err.message : "プレビューに失敗しました");
    },
  });

  const importMutation = useMutation({
    mutationFn: () => callCampaignsApi.importChannelExport(buildImportPayload(false)),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["call-campaigns"] });
      setImportOpen(false);
      setPreview(null);
      setImportSource("");
      setCampaignName("");
      if ("campaignId" in result && result.campaignId) {
        navigate(`/call-campaigns/${result.campaignId}/edit`);
      }
    },
    onError: (err) => {
      setImportError(err instanceof Error ? err.message : "下書き作成に失敗しました");
    },
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Phone size={24} /> 架電キャンペーン
          </h1>
          <p className="text-sm text-slate-500 mt-1">AI自動架電キャンペーンの管理</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-white text-slate-700 border rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Upload size={18} /> リスト取込
          </button>
          <button
            onClick={() => navigate("/call-campaigns/new")}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} /> 新規作成
          </button>
        </div>
      </div>

      {importOpen && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center pt-16 px-4">
          <div className="w-full max-w-3xl bg-white rounded-lg border shadow-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <h2 className="font-semibold text-slate-900">channel-export から下書き作成</h2>
                <p className="text-xs text-slate-500 mt-0.5">sales-analysis の電話投入JSONを Prospect に取り込み、架電キャンペーン下書きを作成します。</p>
              </div>
              <button
                type="button"
                onClick={() => setImportOpen(false)}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                aria-label="閉じる"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">channel-export URL またはJSON</label>
                <textarea
                  value={importSource}
                  onChange={(e) => {
                    setImportSource(e.target.value);
                    setPreview(null);
                    setImportError("");
                  }}
                  className="w-full min-h-32 border rounded px-3 py-2 text-sm font-mono"
                  placeholder="https://call.sphere-light.com/cc/lists/.../channel-export/phone"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">キャンペーン名</label>
                <input
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="未入力の場合はリスト名 + 架電"
                />
              </div>
              {importError && (
                <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded px-3 py-2">
                  {importError}
                </div>
              )}
              {preview && (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b">
                        <th className="text-left bg-slate-50 px-3 py-2 w-40">リスト</th>
                        <td className="px-3 py-2">{preview.title}</td>
                      </tr>
                      <tr className="border-b">
                        <th className="text-left bg-slate-50 px-3 py-2">listSource</th>
                        <td className="px-3 py-2 font-mono text-xs">{preview.listSource}</td>
                      </tr>
                      <tr>
                        <th className="text-left bg-slate-50 px-3 py-2">件数</th>
                        <td className="px-3 py-2">
                          総数 {preview.totalRows} / 発信可 {preview.callableRows} / 除外 {preview.skippedRows} / 抑止 {preview.blockedRows}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-slate-50">
              <button
                type="button"
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending || importMutation.isPending}
                className="px-3 py-2 text-sm bg-white border rounded hover:bg-slate-50 disabled:opacity-50"
              >
                {previewMutation.isPending ? "確認中..." : "プレビュー"}
              </button>
              <button
                type="button"
                onClick={() => importMutation.mutate()}
                disabled={importMutation.isPending || previewMutation.isPending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {importMutation.isPending ? "作成中..." : "下書き作成"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Phone size={48} className="mx-auto mb-4 text-slate-300" />
          <p>架電キャンペーンがありません</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((c) => (
            <div
              key={c.id}
              onClick={() =>
                c.status === "DRAFT"
                  ? navigate(`/call-campaigns/${c.id}/edit`)
                  : navigate(`/call-campaigns/${c.id}`)
              }
              className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[c.status]}`}>
                      {statusLabels[c.status]}
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      {audienceLabels[c.audienceType]}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {c.callScript?.productName || ""}
                    {c.emailCampaign && (
                      <span className="ml-2 text-blue-500">
                        {"→ " + c.emailCampaign.name}
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right text-sm text-slate-500 ml-4 shrink-0">
                  {c.status === "SCHEDULED" && c.scheduledAt && (
                    <p>{format(new Date(c.scheduledAt), "yyyy/MM/dd HH:mm")}</p>
                  )}
                  {c.startedAt && (
                    <p>{format(new Date(c.startedAt), "yyyy/MM/dd HH:mm")}</p>
                  )}
                  {!c.startedAt && !(c.status === "SCHEDULED" && c.scheduledAt) && (
                    <p>{format(new Date(c.createdAt), "yyyy/MM/dd")}</p>
                  )}
                </div>
              </div>

              {c.totalCalls > 0 && (
                <div className="mt-3">
                  <ProgressBar campaign={c} />
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    <span>合計: {c.totalCalls}</span>
                    <span>応答: {c.totalAnswered}</span>
                    <span className="text-green-600">コールバック: {c.totalCallbacks}</span>
                    <span className="text-red-500">失敗: {c.totalFailed}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
